/**
 * @module components/Brand
 * @description Brand marks shared by every card face and page: the AFJ
 * lockup and the postage stamp.
 *
 * Lockups follow AFJ's brand guidelines (_sources/_archive/Final Brand
 * Guidlines.pdf, "Logo" pages): HORIZONTAL for digital interfaces and
 * height-constrained spaces (site header, card headers); VERTICAL STACK for
 * formal, centered placements (pledge panel). The 1A lockup (petals over
 * "AFJ" with a four-line name beside it) is not one of the sanctioned
 * configurations, so it is no longer used.
 *
 * // TEMPORARY: lockup PNGs were rebuilt from the guidelines PDF's embedded
 * raster (1000 px vertical stack, white removed to transparency).
 * What caused this: no vector logo file has been supplied. Why chosen: it is
 * the most faithful source available and is sharp at both print sizes.
 * Side-effects: slight edge softness at 6 × 9 in. Replace with AFJ's
 * SVG/EPS files when received (KI #16).
 */
import { useId } from "react";
import { CAMPAIGN } from "../config/campaign";
import { assetUrl } from "../lib/assetUrl";

export type LockupVariant = "horizontal" | "vertical";

export function BrandLockup({ variant = "horizontal", className }: { variant?: LockupVariant; className?: string }) {
    return (
        <img
            className={`brand-lockup brand-lockup--${variant} ${className ?? ""}`}
            src={assetUrl(`brand/afj-${variant}.png`)}
            alt={CAMPAIGN.organizationName}
        />
    );
}

/** Hummingbird drawn on a 40 × 24 grid. Shared by the stamp and the progress panels. */
export function HummingbirdGlyph() {
    return (
        <>
            <path d="M2 13 L14 11" stroke="#b5452f" strokeWidth="2" strokeLinecap="round" />
            <circle cx="16" cy="11" r="4" fill="#1a2324" />
            <ellipse cx="24" cy="14" rx="9" ry="5" fill="#2e8b57" />
            <path d="M22 11 C26 0 36 -1 39 2 C34 4 29 8 25 12 Z" fill="#1f5d3d" />
            <path d="M31 16 L39 23" stroke="#1a2324" strokeWidth="1.6" />
        </>
    );
}

/** The client's Gala postmark artwork (raster crop from the original print PDF). */
function GalaPostmark({ className }: { className?: string }) {
    return (
        <img
            className={`pc-stamp ${className ?? ""}`}
            src={assetUrl("brand/gala-stamp.png")}
            alt={`${CAMPAIGN.eventName} postmark, ${CAMPAIGN.eventDateLabel}`}
        />
    );
}

/**
 * Year-round postage stamp, drawn in SVG so it is crisp at any print size
 * and carries no Gala date. Same composition as the Gala artwork: a
 * hummingbird stamp with a circular postmark over its left edge.
 */
function CampaignPostmark({ className }: { className?: string }) {
    const uid = useId().replace(/:/g, "");
    const top = `pm-top-${uid}`;
    const bottom = `pm-bot-${uid}`;
    const ink = "var(--c-stamp)";
    return (
        <svg
            className={`pc-stamp pc-stamp--svg ${className ?? ""}`}
            viewBox="0 0 200 132"
            role="img"
            aria-label={`${CAMPAIGN.campaignName} postage stamp, Jamaica`}
        >
            {/* Stamp: perforated edge, mint field, hummingbird, hibiscus, country name. */}
            <rect x="84" y="6" width="110" height="120" fill="#fbf7ee" stroke="var(--c-gold)" strokeWidth="3.2" strokeDasharray="0.1 6.2" strokeLinecap="round" />
            <rect x="92" y="14" width="94" height="104" fill="#e2efe4" stroke="#7d9d86" strokeWidth="1.2" />
            <g transform="translate(108 30) scale(1.9)">
                <HummingbirdGlyph />
            </g>
            <g transform="translate(120 84)" fill="#c0392b">
                <circle r="4" cx="0" cy="-4" /><circle r="4" cx="4" cy="0" /><circle r="4" cx="0" cy="4" /><circle r="4" cx="-4" cy="0" />
                <circle r="2" fill="#f1c40f" />
            </g>
            <text x="139" y="110" textAnchor="middle" fontFamily="var(--ff-serif)" fontWeight="700" fontSize="13" letterSpacing="1.5" fill="var(--c-forest)">
                JAMAICA
            </text>
            {/* Cancellation waves across the stamp. */}
            <g fill="none" stroke={ink} strokeWidth="1.6" opacity="0.75">
                <path d="M96 52 q10 -6 20 0 t20 0 t20 0 t20 0 t20 0" />
                <path d="M96 62 q10 -6 20 0 t20 0 t20 0 t20 0 t20 0" />
                <path d="M96 72 q10 -6 20 0 t20 0 t20 0 t20 0 t20 0" />
            </g>
            {/* Circular postmark: text on arcs, no date (year-round). */}
            <g transform="rotate(-10 62 66)" fill="none" stroke={ink} opacity="0.88">
                <circle cx="62" cy="66" r="52" strokeWidth="2.6" fill="rgb(251 247 238 / 0.55)" />
                <circle cx="62" cy="66" r="30" strokeWidth="1.6" />
                <defs>
                    <path id={top} d="M 22 66 A 40 40 0 0 1 102 66" />
                    <path id={bottom} d="M 21 66 A 41 41 0 0 0 103 66" />
                </defs>
                <text fontFamily="var(--ff-serif)" fontWeight="700" fontSize="11.5" letterSpacing="2.2" fill={ink} stroke="none">
                    <textPath href={`#${top}`} startOffset="50%" textAnchor="middle">TO JAMAICA</textPath>
                </text>
                <text fontFamily="var(--ff-serif)" fontWeight="700" fontSize="11.5" letterSpacing="2.2" fill={ink} stroke="none">
                    <textPath href={`#${bottom}`} startOffset="50%" textAnchor="middle">WITH LOVE</textPath>
                </text>
                <text x="62" y="70" textAnchor="middle" fontFamily="var(--ff-serif)" fontWeight="700" fontSize="17" fill={ink} stroke="none">AFJ</text>
                <text x="62" y="81" textAnchor="middle" fontFamily="var(--ff-serif)" fontSize="6.4" letterSpacing="0.6" fill={ink} stroke="none">SINCE 1982</text>
            </g>
        </svg>
    );
}

/** The postmark for the active edition. */
export function Postmark({ kind, className }: { kind: "gala" | "campaign"; className?: string }) {
    return kind === "gala" ? <GalaPostmark className={className} /> : <CampaignPostmark className={className} />;
}
